import arrayMutators from 'final-form-arrays';
import React, { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';

import { putFieldsValidateSettings } from '../../../api';
import { useConfig } from '../../../contexts/config';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { FormFooter, HeaderTitle, Preloader } from '../../ui';
import { ExtendedProjectConfiguration, ValidationResponse } from '../ValidationRule/types';
import {
  FieldValidationMap,
  getMinLengthFromRegExp,
  mapFormToFields,
  mapSpecialCharacters,
  PASSWORD,
  ValidationFieldName
} from '../ValidationRule/utils';
import { deleteUrl } from './mutators';
import { PasswordConfiguration } from './PasswordConfiguration';
import { ContentForm, FormWrapper, VerticalHorizontalCenterBlock } from './style';

type PasswordSettingsFormTypes = {
  projectId: string;
  currentProjectType: string | null;
  backToNavigation: () => void;
  fieldsValidation: ValidationResponse;
  setFieldsValidation: React.Dispatch<React.SetStateAction<ValidationResponse>>;
  fetchingFieldsValidation: boolean;
};

export const PasswordSettingsForm = ({
  projectId,
  currentProjectType,
  backToNavigation,
  fieldsValidation,
  setFieldsValidation,
  fetchingFieldsValidation
}: PasswordSettingsFormTypes): JSX.Element => {
  const intl = useIntl();
  const { xsollaAnalytics } = useConfig();
  const { updateCurrentProject, currentProject, currentUpdateFetching } =
    useCurrentProject(projectId);
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const [submitError, setSubmitError] = useState<boolean>(false);

  const filterPassword = useMemo(
    () =>
      Object.keys(
        fieldsValidation.find((field) => field.name === ValidationFieldName.PASSWORD_FIELD)
          ?.settings_state || {}
      ).reduce(
        (prev, key) =>
          FieldValidationMap.password.validationRule.includes(key as PASSWORD) && {
            ...prev,
            [key]: true
          },
        {}
      ),
    [fieldsValidation]
  );

  const filterOther = useMemo(
    () =>
      fieldsValidation
        .filter((field) => field.name !== ValidationFieldName.PASSWORD_FIELD)
        .map((field) => ({ ...field, regexps: field.regexps ? field.regexps : [] })),
    [fieldsValidation]
  );

  const minLength = useMemo(
    () => getMinLengthFromRegExp(fieldsValidation, ValidationFieldName.PASSWORD_FIELD),
    [fieldsValidation]
  );

  const specialCharacters = useMemo(
    () => mapSpecialCharacters(fieldsValidation),
    [fieldsValidation]
  );

  const onSubmit = async (values: ExtendedProjectConfiguration) => {
    const {
      fields_validation,
      validation_spec_symbol,
      validation_min_length,
      ...configurationValues
    } = values;

    try {
      setSubmitError(false);
      const passValidation = [
        ...filterOther,
        {
          name: ValidationFieldName.PASSWORD_FIELD,
          ...mapFormToFields({ fields_validation, validation_spec_symbol, validation_min_length })
        }
      ];

      await Promise.all([
        updateCurrentProject(configurationValues),
        putFieldsValidateSettings({ projectId, values: passValidation })
      ]);
      showSuccessNotify();
      xsollaAnalytics.sendClick('login_pa_module__password_validation_page', 'success', {
        passwordValidation: Object.entries(fields_validation)
          .filter(([, field]) => !!field)
          .map(([key]) => key)
      });
      setFieldsValidation(passValidation);
    } catch (err) {
      showErrorNotify();
      xsollaAnalytics.sendClick('login_pa_module__password_validation_page', 'error', {
        error: err
      });
      setSubmitError(true);
    }
  };

  if (fetchingFieldsValidation) {
    return (
      <VerticalHorizontalCenterBlock>
        <Preloader />
      </VerticalHorizontalCenterBlock>
    );
  }

  return (
    <Form
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      initialValues={{
        ...(currentProject || {}),
        fields_validation: filterPassword,
        validation_min_length: minLength,
        validation_spec_symbol: specialCharacters
      }}
      mutators={{
        ...arrayMutators,
        deleteUrl
      }}>
      {({ handleSubmit, form }) => (
        <>
          <ContentForm>
            <HeaderTitle
              title={intl.formatMessage({ id: 'configuration-page.password.page-title' })}
              subtitle={intl.formatMessage({ id: 'configuration-page.password.page-description' })}
              notification={intl.formatMessage({
                id: 'configuration-page.password.page-notification'
              })}
              notificationMargin={8}
            />
            <FormWrapper>
              <PasswordConfiguration deleteUrl={form.mutators.deleteUrl} projectId={projectId} />
            </FormWrapper>
          </ContentForm>

          <FormFooter
            onSubmit={handleSubmit}
            onCancel={backToNavigation}
            fetching={currentUpdateFetching}
            error={submitError}
          />
        </>
      )}
    </Form>
  );
};
